import { FC } from 'react'
import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addRef,
  addState,
  addStateHandlers,
  addWrapper,
  flowMax,
} from 'ad-hok'
import { createUseStyles } from 'react-jss'
import { Trans } from 'react-i18next'
import {
  CategoryFeed,
  FeaturedUsersFeed,
  FeedMedia,
  isFeedItemMedia,
  UserFeed,
} from 'shared/src/api/types'
import { typedAs } from 'shared/src/utils/typedAs'
import { addEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import { BodyScrollLock } from 'shared/src/utils/bodyScrollLocking'
import { addNavigationState } from 'shared/src/utils/addNavigationState'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addFeedContext } from '../context'
import {
  getHasShownRodeoButtonTutorial,
  recordHasShownRodeoButtonTutorial,
} from 'utils/appStorage'
import { ConstrainWidth } from 'utils/layout'
import { RODEO_BUTTON_TUTORIAL_Z_INDEX } from 'utils/zIndex'
import {
  FEED_RIGHT_BAR_BOTTOM_OFFSET,
  FEED_RIGHT_BAR_RIGHT_OFFSET,
} from './Container'
import { ProductsButton } from '../ProductsButton'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import rightArrowIcon from 'assets/right-arrow-icon.svg'
import { MediaState, Media } from './Media'
import { addIsTouchActiveContext } from 'utils/globalTouchListeners'
import { indexIsFlipped } from './util'

const MODAL_TRANSITION_DURATION_MS = 400
const MODAL_TRANSITION_DELAY_MS = 1000

const STEP_TRANSITION_DURATION_MS = 0

const useStyles = createUseStyles({
  layoutContainerInitial: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: RODEO_BUTTON_TUTORIAL_Z_INDEX,
    opacity: 0,
    transitionDuration: `${MODAL_TRANSITION_DURATION_MS}ms`,
    transitionDelay: `${MODAL_TRANSITION_DELAY_MS}ms`,
  },
  layoutContainerTarget: {
    opacity: 1,
  },
  container: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  rodeoTutorialContentContainer: {
    position: 'absolute',
    bottom: FEED_RIGHT_BAR_BOTTOM_OFFSET,
    right: FEED_RIGHT_BAR_RIGHT_OFFSET,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: `${STEP_TRANSITION_DURATION_MS}ms`,
  },
  rodeoTutorialContentContainerVisible: {
    opacity: 1,
    transitionDelay: `${STEP_TRANSITION_DURATION_MS}ms`,
  },
  rodeoInstructionsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  instructions1: {
    fontFamily: serifFontFamily,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '140%',
    color: 'white',
  },
  instructions2: {
    fontFamily: sansSerifFontFamily,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '140%',
    color: 'white',
  },
  rightArrow: {
    alignSelf: 'center',
    marginRight: 33,
    marginLeft: 34,
    position: 'relative',
    top: 3,
    animation: '$bounce 1s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
  },
  '@keyframes bounce': {
    '0%': {
      transform: 'translateX(-2px)',
    },
    '50%': {
      transform: 'translateX(1px)',
    },
    '100%': {
      transform: 'translateX(-2px)',
    },
  },
  mediaOverlayContainerInitial: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: RODEO_BUTTON_TUTORIAL_Z_INDEX,
    opacity: 0,
    transitionDuration: `${MODAL_TRANSITION_DURATION_MS}ms`,
    transitionDelay: `${MODAL_TRANSITION_DELAY_MS}ms`,
  },
  mediaOverlayContainerTarget: {
    opacity: 1,
  },
  rodeoE: {
    fontStyle: 'italic',
  },
})

type FeedType = UserFeed | CategoryFeed | FeaturedUsersFeed

interface RodeoButtonTutorialModalProps {
  media: FeedMedia
  isFlipped: boolean
  onClose: () => void
}

type State = 'initial' | 'firstFeedState' | 'secondFeedState' | 'exiting'

const RodeoButtonTutorialModal: FC<RodeoButtonTutorialModalProps> = flowMax(
  addDisplayName('RodeoButtonTutorialModal'),
  addStateHandlers(
    {
      state: typedAs<State>('initial'),
    },
    {
      onNext:
        ({ state }) =>
        () => ({
          state:
            state === 'initial'
              ? 'firstFeedState'
              : state === 'firstFeedState'
              ? 'secondFeedState'
              : 'exiting',
        }),
    }
  ),
  addNavigationState('feedState', 'setFeedState', typedAs<MediaState>('feed')),
  addEffectOnMount(({ onNext }) => () => {
    onNext()
  }),
  addHandlers({
    onFinishedFadingOut:
      ({ onClose }) =>
      () => {
        onClose()
      },
  }),
  addTranslationHelpers,
  addHandlers({
    onToggleRodeoMode:
      ({ feedState, setFeedState, onNext }) =>
      () => {
        setFeedState(feedState === 'rodeo' ? 'feed' : 'rodeo')
        onNext()
      },
  }),
  addRef('scrollableContainerRef', typedAs<HTMLDivElement | null>(null)),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    state,
    onFinishedFadingOut,
    media,
    isFlipped,
    scrollableContainerRef,
    feedState,
    onToggleRodeoMode,
    classes,
    t,
  }) => (
    <>
      {state !== 'initial' && <BodyScrollLock />}
      <div
        onTransitionEnd={state === 'exiting' ? onFinishedFadingOut : undefined}
        className={cx(
          classes.mediaOverlayContainerInitial,
          state !== 'initial' &&
            state !== 'exiting' &&
            classes.mediaOverlayContainerTarget
        )}
        ref={scrollableContainerRef}
      >
        <ConstrainWidth fillHeight>
          <Media
            media={media}
            isFlipped={isFlipped}
            state={feedState}
            onToggleState={onToggleRodeoMode}
            scrollableContainerElement={scrollableContainerRef.current}
          />
        </ConstrainWidth>
      </div>
      <div
        className={cx(
          classes.layoutContainerInitial,
          state !== 'initial' && classes.layoutContainerTarget
        )}
      >
        <ConstrainWidth
          fillHeight
          positionRelative
          className={classes.container}
        >
          <div
            className={cx(
              classes.rodeoTutorialContentContainer,
              (state === 'firstFeedState' || state === 'secondFeedState') &&
                classes.rodeoTutorialContentContainerVisible
            )}
          >
            <div className={classes.rodeoInstructionsContainer}>
              <div className={classes.instructions1}>
                <Trans
                  t={t}
                  i18nKey={
                    feedState === 'rodeo'
                      ? 'rodeoButtonTutorial.rodeoMode.instructions1'
                      : 'rodeoButtonTutorial.fullScreenMode.instructions1'
                  }
                >
                  <span className={classes.rodeoE} />
                </Trans>
              </div>
              <div className={classes.instructions2}>
                {feedState === 'rodeo'
                  ? t('rodeoButtonTutorial.rodeoMode.instructions2')
                  : t('rodeoButtonTutorial.fullScreenMode.instructions2')}
              </div>
            </div>
            <img src={rightArrowIcon} className={classes.rightArrow} alt="" />
            <ProductsButton
              media={media}
              onClick={onToggleRodeoMode}
              active={feedState === 'rodeo'}
            />
          </div>
        </ConstrainWidth>
      </div>
    </>
  )
)

const STILL_SCROLLING_DETECTION_WINDOW_MS = 50

type AddRodeoButtonTutorial = <
  TProps extends {
    feed: FeedType
  }
>(
  props: TProps
) => TProps

export const addRodeoButtonTutorial: AddRodeoButtonTutorial = flowMax(
  addStateHandlers(
    () => ({
      waitingToShowRodeoButtonTutorial: false,
      showingRodeoButtonTutorial: false,
      hasShownRodeoButtonTutorial: getHasShownRodeoButtonTutorial(),
    }),
    {
      startWaitingToShowRodeoButtonTutorialModal: () => () => ({
        waitingToShowRodeoButtonTutorial: true,
      }),
      openRodeoButtonTutorialModal: () => () => ({
        showingRodeoButtonTutorial: true,
        waitingToShowRodeoButtonTutorial: false,
      }),
      closeRodeoButtonTutorialModal: () => () => ({
        showingRodeoButtonTutorial: false,
        hasShownRodeoButtonTutorial: true,
      }),
    }
  ),
  addEffect(
    ({ hasShownRodeoButtonTutorial }) =>
      () => {
        if (!hasShownRodeoButtonTutorial) return

        recordHasShownRodeoButtonTutorial()
      },
    ['hasShownRodeoButtonTutorial']
  ),
  addFeedContext,
  addState(
    'showingRodeoButtonTutorialForMedia',
    'setShowingRodeoButtonTutorialForMedia',
    typedAs<FeedMedia | null>(null)
  ),
  addProps(({ feed: { media }, showingRodeoButtonTutorialForMedia }) => {
    const mediaIndex = media.findIndex(
      (media) => media === showingRodeoButtonTutorialForMedia
    )

    return {
      isFlipped:
        (showingRodeoButtonTutorialForMedia && indexIsFlipped(mediaIndex)) ??
        false,
    }
  }),
  addEffect(
    ({
        showingRodeoButtonTutorial,
        visibleMediaId,
        setShowingRodeoButtonTutorialForMedia,
        feed: { media },
      }) =>
      () => {
        if (!showingRodeoButtonTutorial) {
          setShowingRodeoButtonTutorialForMedia(null)
          return
        }
        if (!visibleMediaId) return

        const activeMedia = media.find(
          (item) => isFeedItemMedia(item) && item.id === visibleMediaId
        )
        if (!activeMedia || !isFeedItemMedia(activeMedia)) return

        setShowingRodeoButtonTutorialForMedia(activeMedia)
      },
    ['showingRodeoButtonTutorial', 'visibleMediaId']
  ),
  addWrapper(
    (
      render,
      {
        showingRodeoButtonTutorialForMedia,
        isFlipped,
        closeRodeoButtonTutorialModal,
      }
    ) => (
      <>
        {!!showingRodeoButtonTutorialForMedia && (
          <RodeoButtonTutorialModal
            media={showingRodeoButtonTutorialForMedia}
            isFlipped={isFlipped}
            onClose={closeRodeoButtonTutorialModal}
          />
        )}
        {render()}
      </>
    )
  ),
  addState(
    'hasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial',
    'setHasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial',
    false
  ),
  addHandlers({
    markStoppedScrollingIfNoScrollsSinceLastCheck:
      ({
        hasScrolledRef,
        setHasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial,
      }) =>
      (): boolean => {
        if (!hasScrolledRef.current) {
          setHasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial(true)
          return true
        }

        return false
      },
  }),
  addEffect(
    ({
        visibleMediaId,
        showingRodeoButtonTutorial,
        hasShownRodeoButtonTutorial,
        waitingToShowRodeoButtonTutorial,
        startWaitingToShowRodeoButtonTutorialModal,
        setHasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial,
      }) =>
      () => {
        if (!visibleMediaId) return
        if (
          hasShownRodeoButtonTutorial ||
          showingRodeoButtonTutorial ||
          waitingToShowRodeoButtonTutorial
        )
          return

        setHasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial(false)
        startWaitingToShowRodeoButtonTutorialModal()
      },
    ['visibleMediaId']
  ),
  addIsTouchActiveContext,
  addEffect(
    ({
        waitingToShowRodeoButtonTutorial,
        isTouchActive,
        markStoppedScrollingIfNoScrollsSinceLastCheck,
        hasScrolledRef,
      }) =>
      () => {
        if (!(waitingToShowRodeoButtonTutorial && !isTouchActive)) return

        const pollForStoppedScrolling = () => {
          hasScrolledRef.current = false
          setTimeout(() => {
            const stoppedScrolling =
              markStoppedScrollingIfNoScrollsSinceLastCheck()

            if (!stoppedScrolling) {
              pollForStoppedScrolling()
            }
          }, STILL_SCROLLING_DETECTION_WINDOW_MS)
        }
        pollForStoppedScrolling()
      },
    ['waitingToShowRodeoButtonTutorial', 'isTouchActive']
  ),
  addEffect(
    ({
        openRodeoButtonTutorialModal,
        hasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial,
      }) =>
      () => {
        if (!hasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial) return

        openRodeoButtonTutorialModal()
      },
    ['hasStoppedScrollingWhenWaitingToOpenRodeoButtonTutorial']
  )
)
